import { InstagramPostPreview } from '@/components/ui/InstagramPostPreview'
import { LinkedinPostPreview } from '@/components/ui/LinkedinPostPreview'
import { TikTokPostPreview } from '@/components/ui/TikTokPostPreview'
import { WorkspacePreviewFacebook } from '@/components/workspace/workspace-preview/workspace-preview-facebook'
import { Button } from '@/components/ui/shadcn/button'
import { Textarea } from '@/components/ui/shadcn/textarea'
import { type RouterOutput, trpc } from '@/config/trpc'
import { type PostContent as InstagramPostContent } from '@backend/lib/platforms/instagram'
import { type PostContent as LinkedInPostContent } from '@backend/lib/platforms/linkedin'
import { type PostContent as TikTokPostContent } from '@backend/lib/platforms/tiktok'
import { type PostContent as YouTubePostContent } from '@backend/lib/platforms/youtube'
import { type PostContent as FacebookPostContent } from '@backend/lib/platforms/facebook'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'

type ScheduledPost = RouterOutput['scheduledPost']['read']
type PostContent =
  | LinkedInPostContent
  | InstagramPostContent
  | TikTokPostContent
  | YouTubePostContent
  | FacebookPostContent

export const ScheduledPostPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [postContent, setPostContent] = useState<PostContent>()
  const [selectedAccountId, setSelectedAccountId] = useState<string>('')

  //   trpc

  const { data: scheduledPost } = trpc.scheduledPost.read.useQuery(
    { id: Number(id) },
    { enabled: !!id },
  )
  const { mutateAsync: updateScheduledPost } = trpc.scheduledPost.update.useMutation()

  const { data: linkedinData } = trpc.platform.isAuthenticated.useQuery({ platform: 'linkedin' })
  const { data: instagramData } = trpc.platform.isAuthenticated.useQuery({ platform: 'instagram' })
  const { data: tiktokData } = trpc.platform.isAuthenticated.useQuery({ platform: 'tiktok' })
  const { data: facebookData } = trpc.platform.isAuthenticated.useQuery({ platform: 'facebook' })

  const [prevScheduledPost, setPrevScheduledPost] = useState(scheduledPost)
  if (prevScheduledPost !== scheduledPost && scheduledPost) {
    setPrevScheduledPost(scheduledPost)
    setSelectedAccountId(scheduledPost.accountId)
    setPostContent(scheduledPost.postContent)
  }

  const onSave = async () => {
    if (!postContent) return

    if (!isPostContentChanged(scheduledPost, postContent, selectedAccountId)) return

    try {
      if (scheduledPost?.platform === 'linkedin') {
        await updateScheduledPost({
          id: Number(id),
          postContent: postContent as LinkedInPostContent,
          accountId: selectedAccountId,
        })
      } else if (scheduledPost?.platform === 'tiktok') {
        await updateScheduledPost({
          id: Number(id),
          postContent: postContent as TikTokPostContent,
          accountId: selectedAccountId,
        })
      } else if (scheduledPost?.platform === 'youtube') {
        await updateScheduledPost({
          id: Number(id),
          postContent: postContent as YouTubePostContent,
          accountId: selectedAccountId,
        })
      } else if (scheduledPost?.platform === 'facebook') {
        await updateScheduledPost({
          id: Number(id),
          postContent: postContent as FacebookPostContent,
          accountId: selectedAccountId,
        })
      } else {
        await updateScheduledPost({
          id: Number(id),
          postContent: postContent as InstagramPostContent,
        })
      }

      navigate('/posts')
    } catch (error) {
      toast.error('Failed to save changes', {
        description: error instanceof Error ? error.message : 'Unknown error occurred',
      })
    }
  }

  return (
    <section className="mt-[3vh] flex w-full items-center justify-center p-6">
      <div className="flex w-full max-w-[550px] flex-col gap-4">
        <div className="flex w-full items-center justify-between">
          <Button variant="secondary" size="icon" onClick={() => navigate(`/posts`)}>
            <ArrowLeft />
          </Button>
          <Button
            onClick={onSave}
            disabled={!isPostContentChanged(scheduledPost, postContent, selectedAccountId)}>
            Save
          </Button>
        </div>

        {scheduledPost && (
          <div className="">
            {scheduledPost.platform === 'instagram' ? (
              <InstagramPostPreview
                accounts={
                  instagramData?.isAuthenticated === true ? instagramData.accounts : undefined
                }
                postContent={postContent as InstagramPostContent}
                onCaptionChange={(caption) =>
                  setPostContent({
                    ...(postContent as InstagramPostContent),
                    caption,
                  })
                }
                onStartAuth={() => {}}
              />
            ) : scheduledPost.platform === 'tiktok' ? (
              <TikTokPostPreview
                accounts={tiktokData?.isAuthenticated === true ? tiktokData.accounts : undefined}
                selectedAccountId={selectedAccountId}
                postContent={postContent as TikTokPostContent}
                onChangeCaption={(e) =>
                  setPostContent({
                    ...(postContent as TikTokPostContent),
                    caption: e.target.value,
                  })
                }
                onChangeAccountId={setSelectedAccountId}
                onStartAuth={() => {}}
              />
            ) : scheduledPost.platform === 'facebook' ? (
              <div className="flex flex-col gap-3">
                {/* Facebook doesn't have a dedicated edit-in-place preview component
                    here yet (the new modal flow uses WorkspacePreviewFacebook with
                    inline editing). Render a read-only preview plus a caption
                    textarea so the page doesn't crash and basic edits still work. */}
                <Textarea
                  value={(postContent as FacebookPostContent | undefined)?.caption ?? ''}
                  onChange={(e) =>
                    setPostContent({
                      ...(postContent as FacebookPostContent),
                      caption: e.target.value,
                    })
                  }
                  placeholder="Write your Facebook caption..."
                  rows={6}
                />
                <div className="rounded-lg border bg-white">
                  <WorkspacePreviewFacebook
                    account={(() => {
                      const accounts =
                        facebookData?.isAuthenticated === true ? facebookData.accounts : []
                      const acc = accounts.find((a) => a.id === selectedAccountId)
                      return acc
                        ? {
                            id: acc.id,
                            displayName: acc.displayName,
                            imageUrl: acc.imageUrl,
                            subline: acc.subline,
                          }
                        : undefined
                    })()}
                    postContent={postContent as FacebookPostContent}
                    _onStartAuth={() => {}}
                  />
                </div>
              </div>
            ) : (
              <LinkedinPostPreview
                accounts={
                  linkedinData?.isAuthenticated === true ? linkedinData.accounts : undefined
                }
                selectedAccountId={selectedAccountId}
                postContent={postContent as LinkedInPostContent}
                onChangeCaption={(e) =>
                  setPostContent({
                    ...(postContent as LinkedInPostContent),
                    caption: e.target.value,
                  })
                }
                onChangeAccountId={setSelectedAccountId}
                onStartAuth={() => {}}
              />
            )}
          </div>
        )}
      </div>
    </section>
  )
}

const isPostContentChanged = (
  scheduledPost: ScheduledPost | undefined,
  postContent: PostContent | undefined,
  selectedAccountId: string,
) => {
  if (!scheduledPost || !postContent) return false

  if (scheduledPost.platform === 'youtube') {
    const a = postContent as YouTubePostContent
    const b = scheduledPost.postContent as YouTubePostContent
    return a.description !== b.description
  }

  // All remaining platforms (linkedin/instagram/tiktok/facebook) have a `caption` field.
  const a = postContent as
    | InstagramPostContent
    | LinkedInPostContent
    | TikTokPostContent
    | FacebookPostContent
  const b = scheduledPost.postContent as
    | InstagramPostContent
    | LinkedInPostContent
    | TikTokPostContent
    | FacebookPostContent
  return a.caption !== b.caption || scheduledPost.accountId !== selectedAccountId
}
